import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Loader2, BarChart3, RefreshCw, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useBilling } from '@/hooks/useBilling';
import { hasListingScoreCardAccess } from '@/types/billing';

interface ScoreCategory {
  name: string;
  score: number;
  tips: string[];
}

interface ListingScore {
  totalScore: number;
  categories: ScoreCategory[];
  summary: string;
}

interface ListingScoreCardProps {
  listingId: string;
}

function ScoreRing({ score, maxScore = 25, size = 64 }: { score: number; maxScore?: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(score / maxScore, 1);
  const strokeDashoffset = circumference * (1 - percentage);
  
  const getColor = (pct: number) => {
    if (pct >= 0.8) return 'hsl(var(--primary))';
    if (pct >= 0.5) return 'hsl(45 93% 47%)'; // amber
    return 'hsl(0 84% 60%)'; // red
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="4"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor(percentage)}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{score}</span>
      </div>
    </div>
  );
}

export function ListingScoreCard({ listingId }: ListingScoreCardProps) {
  const { billing } = useBilling();
  const [score, setScore] = useState<ListingScore | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasAccess = billing ? hasListingScoreCardAccess(billing) : false;

  const fetchScore = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-listing-score', {
        body: { listingId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setScore(data);
    } catch (err) {
      console.error('Score analysis failed:', err);
      toast({
        title: 'Analysis failed',
        description: err instanceof Error ? err.message : 'Could not analyze listing',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Not a Team subscriber - show upgrade prompt
  if (!hasAccess) {
    return (
      <Card className="border-dashed border-muted-foreground/30">
        <CardContent className="flex items-center gap-4 py-6">
          <div className="p-3 rounded-xl bg-muted">
            <Lock className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">AI Listing Score Card</p>
            <p className="text-xs text-muted-foreground">
              Get AI-powered quality analysis with actionable tips. Available on the Team plan.
            </p>
          </div>
          <Badge variant="outline" className="gap-1 shrink-0">
            <Crown className="w-3 h-3" />
            Team
          </Badge>
        </CardContent>
      </Card>
    );
  }

  // Has access but hasn't analyzed yet
  if (!score) {
    return (
      <Card>
        <CardContent className="flex items-center gap-4 py-6">
          <div className="p-3 rounded-xl bg-primary/10">
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">AI Listing Score Card</p>
            <p className="text-xs text-muted-foreground">
              Analyze your listing quality across content, photos, positioning & completeness.
            </p>
          </div>
          <Button 
            onClick={fetchScore} 
            disabled={isLoading}
            size="sm"
            className="shrink-0"
          >
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
            ) : (
              <><BarChart3 className="w-4 h-4 mr-2" />Analyze</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show results
  const getScoreLabel = (total: number) => {
    if (total >= 85) return { label: 'Excellent', color: 'bg-primary text-primary-foreground' };
    if (total >= 65) return { label: 'Good', color: 'bg-amber-500 text-white' };
    if (total >= 45) return { label: 'Fair', color: 'bg-orange-500 text-white' };
    return { label: 'Needs Work', color: 'bg-destructive text-destructive-foreground' };
  };

  const scoreInfo = getScoreLabel(score.totalScore);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Listing Quality Score
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={scoreInfo.color}>
              {score.totalScore}/100 · {scoreInfo.label}
            </Badge>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchScore} disabled={isLoading}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{score.summary}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {score.categories.map((cat) => (
            <div key={cat.name} className="flex gap-3 p-3 rounded-lg bg-muted/50">
              <ScoreRing score={cat.score} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-1">{cat.name}</p>
                <ul className="space-y-0.5">
                  {cat.tips.map((tip, i) => (
                    <li key={i} className="text-xs text-muted-foreground leading-relaxed">
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
